import React, { useMemo } from 'react'
import { Box, Typography, Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  histogramContainer: {
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(1), // Added extra bottom padding
    marginBottom: theme.spacing(1), // Increased significantly to prevent overlap
    boxShadow: 'none',
    borderBottom: '1px solid #e0e0e0',
    borderRadius: 0,
  },
  histogramTitle: {
    marginBottom: theme.spacing(1),
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  histogramBar: {
    display: 'flex',
    alignItems: 'flex-end',
    height: '60px',
    gap: '1px',
    marginTop: theme.spacing(3),
  },
  bar: {
    flex: 1,
    backgroundColor: theme.palette.primary.main,
    minHeight: '2px',
    borderRadius: '1px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scaleY(1.1)',
      '& .count-label': {
        opacity: 1,
      },
    },
  },
  timeLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    fontSize: '11px',
    color: theme.palette.text.secondary,
    paddingLeft: '4px',
    paddingRight: '4px',
  },
  severityBar: {
    flex: 1,
    minHeight: '2px',
    borderRadius: '1px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scaleY(1.1)',
    },
  },
  errorBar: {
    backgroundColor: '#f44336', // Red for errors
  },
  warningBar: {
    backgroundColor: '#ff9800', // Orange for warnings
  },
  infoBar: {
    backgroundColor: '#2196f3', // Blue for info
  },
  debugBar: {
    backgroundColor: '#9e9e9e', // Grey for debug
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2), // Increased from 1 to 2
    marginBottom: theme.spacing(1), // Added bottom margin
    fontSize: '11px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
}))

const LogHistogram = ({ logs = [], timeRange = '1h', onTimeRangeClick }) => {
  const classes = useStyles()

  // Debug logging to see what logs data looks like
  console.log('LogHistogram logs data:', {
    logs,
    type: typeof logs,
    isArray: Array.isArray(logs),
    length: logs?.length,
    sample: logs?.[0]
  })

  const histogramData = useMemo(() => {
    if (!logs || !Array.isArray(logs) || logs.length === 0) return []

    const now = moment()
    let interval, buckets, startTime

    // Use the timeRange prop to determine the time window
    switch (timeRange) {
      case '1h':
        interval = 5 // 5-minute intervals
        buckets = 12
        startTime = now.clone().subtract(1, 'hour')
        break
      case '6h':
        interval = 30 // 30-minute intervals
        buckets = 12
        startTime = now.clone().subtract(6, 'hours')
        break
      case '24h':
        interval = 60 // 1-hour intervals
        buckets = 24
        startTime = now.clone().subtract(24, 'hours')
        break
      case '7d':
        interval = 24 * 60 // 1-day intervals
        buckets = 7
        startTime = now.clone().subtract(7, 'days')
        break
      default:
        interval = 5
        buckets = 12
        startTime = now.clone().subtract(1, 'hour')
    }

    // Create time buckets based on the selected time range
    const bucketsData = []
    for (let i = 0; i < buckets; i++) {
      const bucketTime = startTime.clone().add(i * interval, 'minutes')
      bucketsData.push({
        time: bucketTime,
        count: 0,
        errors: 0,
        warnings: 0,
        info: 0,
        debug: 0,
      })
    }

    // Count logs in each bucket
    logs.forEach(log => {
      if (!log || !log.timestamp) return // Skip invalid log entries
      
      const logTime = moment(log.timestamp)
      if (!logTime.isValid()) return // Skip invalid timestamps
      
      const bucket = bucketsData.find(b => 
        logTime.isBetween(b.time, moment(b.time).add(interval, 'minutes'), null, '[)')
      )
      
      if (bucket) {
        bucket.count++
        
        // Count by severity
        const severity = (log.severity || 'info').toLowerCase()
        switch (severity) {
          case 'error':
          case 'err':
            bucket.errors++
            break
          case 'warning':
          case 'warn':
            bucket.warnings++
            break
          case 'info':
            bucket.info++
            break
          case 'debug':
            bucket.debug++
            break
          default:
            bucket.info++
        }
      }
    })

    return bucketsData
  }, [logs, timeRange])

  const maxCount = Math.max(...histogramData.map(b => b.count), 1)

  // Debug logging
  console.log('Histogram data:', {
    timeRange,
    bucketsCount: histogramData.length,
    maxCount,
    sampleBuckets: histogramData.slice(0, 3).map(b => ({
      time: b.time.format('YYYY-MM-DD HH:mm'),
      count: b.count
    }))
  })

  const handleBarClick = (bucket) => {
    if (onTimeRangeClick) {
      onTimeRangeClick(bucket.time, moment(bucket.time).add(5, 'minutes'))
    }
  }

  if (histogramData.length === 0) {
    return (
      <Paper className={classes.histogramContainer}>
        <Typography className={classes.histogramTitle}>
          Log Activity (No data available)
        </Typography>
      </Paper>
    )
  }

  // Get time range display text
  const getTimeRangeText = () => {
    switch (timeRange) {
      case '1h': return 'Last 1 hour'
      case '6h': return 'Last 6 hours'
      case '24h': return 'Last 24 hours'
      case '7d': return 'Last 7 days'
      default: return 'Last 1 hour'
    }
  }

  return (
    <Paper className={classes.histogramContainer}>
      <Typography className={classes.histogramTitle}>
        Log Activity - {getTimeRangeText()}
      </Typography>
      
      <Box className={classes.histogramBar}>
        {histogramData.map((bucket, index) => (
          <Box
            key={index}
            className={classes.bar}
            style={{
              height: `${Math.max((bucket.count / maxCount) * 100, 2)}%`,
              backgroundColor: bucket.count === 0 ? '#f5f5f5' : '#e0e0e0',
              position: 'relative',
            }}
            onClick={() => handleBarClick(bucket)}
            title={`${bucket.time.format('YYYY-MM-DD HH:mm')}: ${bucket.count} logs`}
          >
            {/* Show count label on hover or if count > 0 */}
            {bucket.count > 0 && (
              <Box
                style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '10px',
                  color: '#666',
                  whiteSpace: 'nowrap',
                  opacity: bucket.count > 0 ? 1 : 0, // Always show if count > 0
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none',
                  fontWeight: 'bold',
                }}
                className="count-label"
              >
                {bucket.count}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Box className={classes.timeLabels}>
        {histogramData.map((bucket, index) => {
          // Show labels for first, last, and every 1/4 of the data
          const shouldShowLabel = 
            index === 0 || 
            index === histogramData.length - 1 || 
            index % Math.ceil(histogramData.length / 4) === 0 ||
            histogramData.length <= 8 // Show all labels if 8 or fewer buckets

          if (!shouldShowLabel) return <span key={index}></span>

          // Check if we span multiple days
          const firstDate = histogramData[0]?.time.format('YYYY-MM-DD')
          const lastDate = histogramData[histogramData.length - 1]?.time.format('YYYY-MM-DD')
          const spansMultipleDays = firstDate !== lastDate

          // Format label based on time range and whether it spans multiple days
          let label
          if (timeRange === '7d' || spansMultipleDays) {
            // Show date for multi-day ranges
            label = bucket.time.format('MM/DD')
          } else if (timeRange === '24h') {
            // For single day, show time
            label = bucket.time.format('HH:mm')
          } else if (timeRange === '6h') {
            label = bucket.time.format('HH:mm')
          } else {
            // 1h or default
            label = bucket.time.format('HH:mm')
          }

          return <span key={index}>{label}</span>
        })}
      </Box>

      <Box className={classes.legend}>
        <Box className={classes.legendItem}>
          <Box className={`${classes.legendColor} ${classes.errorBar}`} />
          <span>Error</span>
        </Box>
        <Box className={classes.legendItem}>
          <Box className={`${classes.legendColor} ${classes.warningBar}`} />
          <span>Warning</span>
        </Box>
        <Box className={classes.legendItem}>
          <Box className={`${classes.legendColor} ${classes.infoBar}`} />
          <span>Info</span>
        </Box>
        <Box className={classes.legendItem}>
          <Box className={`${classes.legendColor} ${classes.debugBar}`} />
          <span>Debug</span>
        </Box>
      </Box>
    </Paper>
  )
}

export default LogHistogram
